import React, { useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { USAGE_TYPE } from "../common";
import dayjs from "dayjs";
import { ContextMenu } from 'primereact/contextmenu';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { deletePricing } from '../../../service/billingService';

interface PricingData {
  id: string;
  usage_type: number;
  cost_system_per_unit: string;
  cost_client_per_unit: string;
  active: boolean;
  effective_date: string;
  created_at: string;
}

interface PricingTableProps {
  data: PricingData[];
  loading: boolean;
  onNewClick: () => void;
  onRefresh: () => void;
  showDelete: boolean;
}

export default function PricingTable({ data, loading, onNewClick, onRefresh, showDelete }: PricingTableProps) {
  const [selectedRow, setSelectedRow] = useState<PricingData | null>(null);
  const cm = useRef<ContextMenu>(null);
  const toast = useRef(null as any);

  const renderHeader = () => {
    return (
      <div style={{display: 'flex', justifyContent: 'end'}}>
        <Button
          type="button"
          label="New Client Cost"
          className="p-button-outlined"
          size="small"
          style={{ fontSize: 'small', marginRight: 10, color: "#6030ff" }}
          onClick={onNewClick}
        />
      </div>
    );
  };

  const usageTypeTemplate = (rowData: PricingData) => {
    return USAGE_TYPE.find(item => item.value === rowData.usage_type)?.label;
  };

  const costTemplate = (value: string) => {
    return `R${parseFloat(value).toFixed(2)}`;
  };

  const dateTemplate = (value: string) => {
    return dayjs(value).format('YYYY/MM/DD');
  };

  const deleteConfirm = () => {
    if (!selectedRow) return;

    confirmDialog({
      message: 'Are you sure you want to delete this pricing?',
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await deletePricing(selectedRow.id);
          toast.current.show({ severity: 'success', summary: 'Pricing Deleted Successfully', life: 3000 });
          onRefresh();
        } catch (error) {
          toast.current.show({ severity: 'error', summary: 'Error Deleting Pricing', life: 3000 });
        }
      }
    });
  };

  const contextItems = [
    { label: 'Delete', icon: 'pi pi-fw pi-trash', command: deleteConfirm }
  ];

  return (
    <React.Fragment>
      {showDelete && <ContextMenu model={contextItems} ref={cm} />}

      <DataTable
        value={data}
        loading={loading}
        className="p-datatable-sm"
        header={renderHeader()}
        selectionMode="single"
        selection={selectedRow}
        onSelectionChange={e => setSelectedRow(e.value as PricingData)}
        onContextMenuSelectionChange={e => setSelectedRow(e.value as PricingData)}
        onContextMenu={e => showDelete ? cm.current?.show(e.originalEvent) : null}
      >
        <Column field="usage_type" header="Usage Type" body={usageTypeTemplate} sortable />
        <Column
          field="cost_system_per_unit"
          header="System Cost"
          body={(rowData) => costTemplate(rowData.cost_system_per_unit)}
          sortable
        />
        <Column
          field="cost_client_per_unit"
          header="Client Cost"
          body={(rowData) => costTemplate(rowData.cost_client_per_unit)}
          sortable
        />
        <Column
          field="effective_date"
          header="Effective Date"
          body={(rowData) => dateTemplate(rowData.effective_date)}
          sortable
        />
        {/*<Column*/}
        {/*  field="created_at"*/}
        {/*  header="Created At"*/}
        {/*  body={(rowData) => dateTemplate(rowData.created_at)}*/}
        {/*  sortable*/}
        {/*/>*/}
      </DataTable>

      <ConfirmDialog />
      <Toast ref={toast} />
    </React.Fragment>
  );
}
